import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '../../context/AuthContext';
import styles from './AdminDashboard.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const ADMIN_USER_ID = 'dm3QY2yNVBU4zKwJ1Nx8GHYuPWI2'; // Change to your admin UID

export default function AdminDashboard() {
  const [pendingResources, setPendingResources] = useState([]);
  const [approvedResources, setApprovedResources] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');

  // Toast state
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [pendingRes, approvedRes, collectionsRes] = await Promise.all([
        fetch(`${API_URL}/resources/pending`, { headers: { 'x-user-id': ADMIN_USER_ID } }),
        fetch(`${API_URL}/resources/approved`, { headers: { 'x-user-id': ADMIN_USER_ID } }),
        fetch(`${API_URL}/collections`)
      ]);

      setPendingResources(await pendingRes.json());
      setApprovedResources(await approvedRes.json());
      setCollections(await collectionsRes.json());
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = async (resourceId, status) => {
    if (!window.confirm(`Mark this resource as ${status}?`)) return;

    try {
      await fetch(`${API_URL}/resources/${resourceId}/moderate`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': ADMIN_USER_ID
        },
        body: JSON.stringify({ status })
      });

      showToast(`Resource ${status} successfully`, 'success');
      fetchAllData();
    } catch (err) {
      showToast('Failed to update resource status', 'error');
    }
  };

  const handleAddToCollection = async (resourceId, collectionId) => {
    if (!collectionId) return;

    try {
      await fetch(`${API_URL}/resources/${resourceId}/add-to-collection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': ADMIN_USER_ID
        },
        body: JSON.stringify({ collectionId })
      });

      showToast('Resource added to collection', 'success');
      fetchAllData();
    } catch (err) {
      showToast('Failed to add resource to collection', 'error');
    }
  };

  const handleRemoveFromCollection = async (resourceId, collectionId) => {
    if (!collectionId) return;

    try {
      await fetch(`${API_URL}/resources/${resourceId}/remove-from-collection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': ADMIN_USER_ID
        },
        body: JSON.stringify({ collectionId })
      });

      showToast('Resource removed from collection', 'success');
      fetchAllData();
    } catch (err) {
      showToast('Failed to remove resource from collection', 'error');
    }
  };

  if (loading) {
    return (
      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.loading}>
            <Icon icon="lucide:loader-2" className={styles.spinner} />
            <p>Loading dashboard...</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        <h1 className={styles.title}>Admin Dashboard</h1>
        <p className={styles.subtitle}>Manage resources and collections</p>

        {/* Tabs */}
        <div className={styles.tabs}>
          <button 
            onClick={() => setActiveTab('pending')} 
            className={activeTab === 'pending' ? styles.activeTab : styles.tabBtn}
          >
            Pending Resources
          </button>
          <button 
            onClick={() => setActiveTab('approved')} 
            className={activeTab === 'approved' ? styles.activeTab : styles.tabBtn}
          >
            Approved Resources
          </button>
          <button 
            onClick={() => setActiveTab('collections')} 
            className={activeTab === 'collections' ? styles.activeTab : styles.tabBtn}
          >
            Collections
          </button>
        </div>

        {/* Toast Notification */}
        {toast && (
          <div className={`${styles.toast} ${styles[toast.type]}`}>
            <Icon icon={toast.type === 'success' ? "lucide:check-circle" : "lucide:alert-circle"} />
            {toast.message}
          </div>
        )}

        {/* Pending Tab */}
        {activeTab === 'pending' && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Pending Resources</h2>
            <div className={styles.resourceGrid}>
              {pendingResources.map(res => (
                <div key={res.id} className={styles.card}>
                  <div className={styles.cardHeader}>
                    <span className={styles.cardType}>{res.type}</span>
                    <span className={styles.cardDate}>
                      {new Date(res.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <h3>{res.title}</h3>
                  <p className={styles.cardDesc}>{res.description?.substring(0, 120)}...</p>

                  <div className={styles.cardActions}>
                    <button onClick={() => handleModerate(res.id, 'approved')} className={styles.btnApprove}>
                      <Icon icon="lucide:check" /> Approve
                    </button>
                    <button onClick={() => handleModerate(res.id, 'rejected')} className={styles.btnReject}>
                      <Icon icon="lucide:x" /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Approved Tab */}
        {activeTab === 'approved' && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Approved Resources • Manage Collections</h2>
            <div className={styles.resourceGrid}>
              {approvedResources.map(res => (
                <div key={res.id} className={styles.card}>
                  <h3>{res.title}</h3>
                  <p className={styles.cardDesc}>{res.description?.substring(0, 110)}...</p>

                  <div className={styles.collectionActions}>
                    <select 
                      onChange={(e) => handleAddToCollection(res.id, e.target.value)}
                      defaultValue=""
                    >
                      <option value="">Add to Collection...</option>
                      {collections.map(col => (
                        <option key={col.id} value={col.id}>
                          {col.title}
                        </option>
                      ))}
                    </select>

                    {res.collections && res.collections.length > 0 && (
                      <div className={styles.currentCollections}>
                        In: {res.collections.map(c => c.title).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Collections Tab */}
        {activeTab === 'collections' && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>All Collections</h2>
            <div className={styles.collectionGrid}>
              {collections.map(col => (
                <div key={col.id} className={styles.card}>
                  <h3>{col.title}</h3>
                  <p>{col.description}</p>
                  <p><strong>{col.resources?.length || 0}</strong> resources</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
// import { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom';
// import { Icon } from '@iconify/react';
// import { useAuth } from '../../context/AuthContext';
// import styles from './AdminDashboard.module.css';

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
// const ADMIN_USER_ID = 'dm3QY2yNVBU4zKwJ1Nx8GHYuPWI2'; // ← Replace with your admin UID

// export default function AdminDashboard() {
//   const [pendingResources, setPendingResources] = useState([]);
//   const [approvedResources, setApprovedResources] = useState([]);
//   const [collections, setCollections] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [activeTab, setActiveTab] = useState('pending');

//   useEffect(() => {
//     fetchAllData();
//   }, []);

//   const fetchAllData = async () => {
//     try {
//       setLoading(true);

//       // Pending resources
//       const resPending = await fetch(`${API_URL}/resources/pending`, {
//         headers: { 'x-user-id': ADMIN_USER_ID }
//       });
//       const pending = await resPending.json();
//       setPendingResources(pending);

//       // Approved resources
//       const resApproved = await fetch(`${API_URL}/resources/approved`, {
//         headers: { 'x-user-id': ADMIN_USER_ID }
//       });
//       const approved = await resApproved.json();
//       setApprovedResources(approved);

//       // All collections
//       const resCollections = await fetch(`${API_URL}/collections`);
//       const cols = await resCollections.json();
//       setCollections(cols);

//     } catch (err) {
//       setError(err.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleModerate = async (resourceId, status) => {
//     if (!window.confirm(`Mark as ${status}?`)) return;

//     try {
//       await fetch(`${API_URL}/resources/${resourceId}/moderate`, {
//         method: 'PUT',
//         headers: {
//           'Content-Type': 'application/json',
//           'x-user-id': ADMIN_USER_ID
//         },
//         body: JSON.stringify({ status })
//       });
//       fetchAllData(); // refresh
//     } catch (err) {
//       alert('Failed to update status');
//     }
//   };

//   const handleAddToCollection = async (resourceId, collectionId) => {
//     try {
//       await fetch(`${API_URL}/resources/${resourceId}/add-to-collection`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           'x-user-id': ADMIN_USER_ID
//         },
//         body: JSON.stringify({ collectionId })
//       });
//       alert('Added to collection');
//       fetchAllData();
//     } catch (err) {
//       alert('Failed to add');
//     }
//   };

//   const handleRemoveFromCollection = async (resourceId, collectionId) => {
//     try {
//       await fetch(`${API_URL}/resources/${resourceId}/remove-from-collection`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           'x-user-id': ADMIN_USER_ID
//         },
//         body: JSON.stringify({ collectionId })
//       });
//       alert('Removed from collection');
//       fetchAllData();
//     } catch (err) {
//       alert('Failed to remove');
//     }
//   };

//   if (loading) return <div className={styles.loading}>Loading dashboard...</div>;
//   if (error) return <div className={styles.error}>Error: {error}</div>;

//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         <h1 className={styles.title}>Admin Dashboard</h1>

//         <div className={styles.tabs}>
//           <button onClick={() => setActiveTab('pending')} className={activeTab === 'pending' ? styles.activeTab : ''}>
//             Pending Resources
//           </button>
//           <button onClick={() => setActiveTab('approved')} className={activeTab === 'approved' ? styles.activeTab : ''}>
//             Approved Resources
//           </button>
//           <button onClick={() => setActiveTab('collections')} className={activeTab === 'collections' ? styles.activeTab : ''}>
//             Collections
//           </button>
//         </div>

//         {/* Pending Tab */}
//         {activeTab === 'pending' && (
//           <div className={styles.section}>
//             <h2>Pending Resources</h2>
//             {pendingResources.map(res => (
//               <div key={res.id} className={styles.card}>
//                 <h3>{res.title}</h3>
//                 <p>{res.description.substring(0, 100)}...</p>
//                 <div className={styles.actions}>
//                   <button onClick={() => handleModerate(res.id, 'approved')} className={styles.btnApprove}>Approve</button>
//                   <button onClick={() => handleModerate(res.id, 'rejected')} className={styles.btnReject}>Reject</button>
//                 </div>
//               </div>
//             ))}
//           </div>
//         )}

//         {/* Approved Tab - Manage Collections */}
//         {activeTab === 'approved' && (
//           <div className={styles.section}>
//             <h2>Approved Resources - Add/Remove from Collections</h2>
//             {approvedResources.map(res => (
//               <div key={res.id} className={styles.card}>
//                 <h3>{res.title}</h3>
//                 <p>{res.description.substring(0, 100)}...</p>

//                 <div className={styles.collectionActions}>
//                   <select onChange={(e) => handleAddToCollection(res.id, e.target.value)} defaultValue="">
//                     <option value="">Add to Collection...</option>
//                     {collections.map(col => (
//                       <option key={col.id} value={col.id}>{col.title}</option>
//                     ))}
//                   </select>

//                   {res.collections && res.collections.length > 0 && (
//                     <div>
//                       Currently in: {res.collections.map(c => c.title).join(', ')}
//                       <button onClick={() => handleRemoveFromCollection(res.id, res.collections[0].id)}>
//                         Remove from first collection
//                       </button>
//                     </div>
//                   )}
//                 </div>
//               </div>
//             ))}
//           </div>
//         )}

//         {/* Collections Tab */}
//         {activeTab === 'collections' && (
//           <div className={styles.section}>
//             <h2>Collections</h2>
//             {collections.map(col => (
//               <div key={col.id} className={styles.card}>
//                 <h3>{col.title}</h3>
//                 <p>{col.description}</p>
//                 <p><strong>{col.resources?.length || 0} resources</strong></p>
//               </div>
//             ))}
//           </div>
//         )}
//       </div>
//     </main>
//   );
// }
// import { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom';
// import { Icon } from '@iconify/react';
// import styles from './AdminDashboard.module.css';

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
// const ADMIN_USER_ID = 'admin_user_id_123'; // ← REPLACE with your real admin ID from auth

// export default function AdminDashboard() {
//   const [pendingResources, setPendingResources] = useState([]);
//   const [collections, setCollections] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [activeTab, setActiveTab] = useState('resources');

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         setLoading(true);

//         // Fetch pending resources
//         const resResources = await fetch(`${API_URL}/resources/pending`, {
//           headers: { 'x-user-id': ADMIN_USER_ID }
//         });
//         if (!resResources.ok) throw new Error(`Pending resources failed: ${resResources.status}`);
//         const pending = await resResources.json();
//         setPendingResources(pending);

//         // Fetch all collections
//         const resCollections = await fetch(`${API_URL}/collections`);
//         if (!resCollections.ok) throw new Error(`Collections failed: ${resCollections.status}`);
//         const cols = await resCollections.json();
//         setCollections(cols);

//       } catch (err) {
//         setError(err.message);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   const handleModerate = async (resourceId, newStatus) => {
//     if (!window.confirm(`Are you sure you want to ${newStatus} this resource?`)) return;

//     try {
//       const res = await fetch(`${API_URL}/resources/${resourceId}/moderate`, {
//         method: 'PUT',
//         headers: {
//           'Content-Type': 'application/json',
//           'x-user-id': ADMIN_USER_ID
//         },
//         body: JSON.stringify({ status: newStatus })
//       });

//       if (!res.ok) {
//         const errData = await res.json();
//         throw new Error(errData.error || 'Moderation failed');
//       }

//       // Refresh list
//       setPendingResources(prev => prev.filter(r => r.id !== resourceId));
//       alert(`Resource marked as ${newStatus}!`);
//     } catch (err) {
//       alert('Error: ' + err.message);
//     }
//   };

//   if (loading) {
//     return (
//       <main className={styles.main}>
//         <div className={styles.container}>
//           <div className={styles.loading}>
//             <Icon icon="lucide:loader-2" className={styles.spinner} />
//             Loading dashboard...
//           </div>
//         </div>
//       </main>
//     );
//   }

//   if (error) {
//     return (
//       <main className={styles.main}>
//         <div className={styles.container}>
//           <div className={styles.error}>
//             <Icon icon="lucide:alert-triangle" />
//             {error}
//           </div>
//         </div>
//       </main>
//     );
//   }

//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         <header className={styles.header}>
//           <h1 className={styles.title}>Admin Dashboard</h1>
//           <p className={styles.subtitle}>Manage submitted resources and collections</p>

//           <div className={styles.tabs}>
//             <button
//               className={`${styles.tabBtn} ${activeTab === 'resources' ? styles.activeTab : ''}`}
//               onClick={() => setActiveTab('resources')}
//             >
//               Pending Resources
//             </button>
//             <button
//               className={`${styles.tabBtn} ${activeTab === 'collections' ? styles.activeTab : ''}`}
//               onClick={() => setActiveTab('collections')}
//             >
//               Collections Management
//             </button>
//           </div>
//         </header>

//         {activeTab === 'resources' && (
//           <section className={styles.section}>
//             <h2 className={styles.sectionTitle}>Pending Resource Submissions</h2>

//             {pendingResources.length === 0 ? (
//               <div className={styles.empty}>
//                 <Icon icon="lucide:inbox" />
//                 <p>No pending resources to review</p>
//               </div>
//             ) : (
//               <div className={styles.resourceGrid}>
//                 {pendingResources.map((res) => (
//                   <div key={res.id} className={styles.resourceCard}>
//                     <div className={styles.cardHeader}>
//                       <span className={styles.cardType}>{res.type}</span>
//                       <span className={styles.cardDate}>
//                         Submitted {new Date(res.createdAt).toLocaleDateString()}
//                       </span>
//                     </div>

//                     <h3 className={styles.cardTitle}>{res.title}</h3>
//                     <p className={styles.cardDesc}>
//                       {res.description.length > 150
//                         ? res.description.substring(0, 150) + '...'
//                         : res.description}
//                     </p>

//                     <div className={styles.cardMeta}>
//                       <span>From: {res.submittedBy || 'Anonymous'}</span>
//                       <span>Languages: {res.languages?.join(', ') || 'N/A'}</span>
//                     </div>

//                     <div className={styles.cardActions}>
//                       <button
//                         className={styles.btnApprove}
//                         onClick={() => handleModerate(res.id, 'approved')}
//                       >
//                         <Icon icon="lucide:check" />
//                         Approve
//                       </button>
//                       <button
//                         className={styles.btnReject}
//                         onClick={() => handleModerate(res.id, 'rejected')}
//                       >
//                         <Icon icon="lucide:x" />
//                         Reject
//                       </button>
//                       <Link to={`/resource/${res.id}`} className={styles.btnView}>
//                         <Icon icon="lucide:eye" />
//                         View Details
//                       </Link>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </section>
//         )}

//         {activeTab === 'collections' && (
//           <section className={styles.section}>
//             <h2 className={styles.sectionTitle}>Manage Collections</h2>

//             <div className={styles.collectionGrid}>
//               {collections.map((col) => (
//                 <div key={col.id} className={styles.collectionCard}>
//                   <h3 className={styles.colTitle}>{col.title}</h3>
//                   <p className={styles.colDesc}>
//                     {col.description.substring(0, 120)}...
//                   </p>
//                   <div className={styles.colMeta}>
//                     <span>{col.resources?.length || 0} resources</span>
//                     <span>Tags: {col.tags?.join(', ') || 'None'}</span>
//                   </div>
//                   <div className={styles.colActions}>
//                     <Link to={`/collections/${col.slug}`} className={styles.btnView}>
//                       View Collection
//                     </Link>
//                     <button className={styles.btnEdit}>
//                       <Icon icon="lucide:edit" />
//                       Edit
//                     </button>
//                   </div>
//                 </div>
//               ))}
//             </div>

//             <div className={styles.addSection}>
//               <button className={styles.btnAdd}>
//                 <Icon icon="lucide:plus" />
//                 Create New Collection
//               </button>
//             </div>
//           </section>
//         )}
//       </div>
//     </main>
//   );
// }

//older
// import { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom';
// import { Icon } from '@iconify/react';
// import styles from './AdminDashboard.module.css';

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// export default function AdminDashboard() {
//   const [pendingResources, setPendingResources] = useState([]);
//   const [collections, setCollections] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [activeTab, setActiveTab] = useState('resources'); // 'resources' or 'collections'

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         setLoading(true);

//         // Fetch pending resources
//         const resResources = await fetch(`${API_URL}/resources/pending`, {
//           headers: { 'x-user-id': 'admin_user_id_123' } // ← Replace with your admin user ID
//         });
//         if (!resResources.ok) throw new Error('Failed to fetch pending resources');
//         const pending = await resResources.json();
//         setPendingResources(pending);

//         // Fetch all collections (for management)
//         const resCollections = await fetch(`${API_URL}/collections`);
//         if (!resCollections.ok) throw new Error('Failed to fetch collections');
//         const cols = await resCollections.json();
//         setCollections(cols);

//       } catch (err) {
//         setError(err.message);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   const handleModerate = async (resourceId, newStatus) => {
//     if (!window.confirm(`Are you sure you want to ${newStatus} this resource?`)) return;

//     try {
//       const res = await fetch(`${API_URL}/resources/${resourceId}/moderate`, {
//         method: 'PUT',
//         headers: {
//           'Content-Type': 'application/json',
//           'x-user-id': 'admin_user_id_123' // ← Replace with admin ID
//         },
//         body: JSON.stringify({ status: newStatus })
//       });

//       if (!res.ok) throw new Error('Moderation failed');
//       const updated = await res.json();

//       // Refresh list
//       setPendingResources(prev => prev.filter(r => r.id !== resourceId));
//       alert(`Resource ${newStatus}!`);
//     } catch (err) {
//       alert('Error: ' + err.message);
//     }
//   };

//   if (loading) {
//     return (
//       <main className={styles.main}>
//         <div className={styles.container}>
//           <div className={styles.loading}>
//             <Icon icon="lucide:loader-2" className={styles.spinner} />
//             Loading dashboard...
//           </div>
//         </div>
//       </main>
//     );
//   }

//   if (error) {
//     return (
//       <main className={styles.main}>
//         <div className={styles.container}>
//           <div className={styles.error}>
//             <Icon icon="lucide:alert-triangle" />
//             {error}
//           </div>
//         </div>
//       </main>
//     );
//   }

//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         <header className={styles.header}>
//           <h1 className={styles.title}>Admin Dashboard</h1>
//           <p className={styles.subtitle}>Manage submitted resources and collections</p>

//           <div className={styles.tabs}>
//             <button
//               className={`${styles.tabBtn} ${activeTab === 'resources' ? styles.activeTab : ''}`}
//               onClick={() => setActiveTab('resources')}
//             >
//               Pending Resources
//             </button>
//             <button
//               className={`${styles.tabBtn} ${activeTab === 'collections' ? styles.activeTab : ''}`}
//               onClick={() => setActiveTab('collections')}
//             >
//               Collections Management
//             </button>
//           </div>
//         </header>

//         {activeTab === 'resources' && (
//           <section className={styles.section}>
//             <h2 className={styles.sectionTitle}>Pending Resource Submissions</h2>

//             {pendingResources.length === 0 ? (
//               <div className={styles.empty}>
//                 <Icon icon="lucide:inbox" />
//                 <p>No pending resources to review</p>
//               </div>
//             ) : (
//               <div className={styles.resourceGrid}>
//                 {pendingResources.map((res) => (
//                   <div key={res.id} className={styles.resourceCard}>
//                     <div className={styles.cardHeader}>
//                       <span className={styles.cardType}>{res.type}</span>
//                       <span className={styles.cardDate}>
//                         Submitted {new Date(res.createdAt).toLocaleDateString()}
//                       </span>
//                     </div>

//                     <h3 className={styles.cardTitle}>{res.title}</h3>
//                     <p className={styles.cardDesc}>
//                       {res.description.length > 150
//                         ? res.description.substring(0, 150) + '...'
//                         : res.description}
//                     </p>

//                     <div className={styles.cardMeta}>
//                       <span>From: {res.submittedBy || 'Anonymous'}</span>
//                       <span>Languages: {res.languages?.join(', ') || 'N/A'}</span>
//                     </div>

//                     <div className={styles.cardActions}>
//                       <button
//                         className={styles.btnApprove}
//                         onClick={() => handleModerate(res.id, 'approved')}
//                       >
//                         <Icon icon="lucide:check" />
//                         Approve
//                       </button>
//                       <button
//                         className={styles.btnReject}
//                         onClick={() => handleModerate(res.id, 'rejected')}
//                       >
//                         <Icon icon="lucide:x" />
//                         Reject
//                       </button>
//                       <Link to={`/resource/${res.id}`} className={styles.btnView}>
//                         <Icon icon="lucide:eye" />
//                         View Details
//                       </Link>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </section>
//         )}

//         {activeTab === 'collections' && (
//           <section className={styles.section}>
//             <h2 className={styles.sectionTitle}>Manage Collections</h2>

//             <div className={styles.collectionGrid}>
//               {collections.map((col) => (
//                 <div key={col.id} className={styles.collectionCard}>
//                   <h3 className={styles.colTitle}>{col.title}</h3>
//                   <p className={styles.colDesc}>
//                     {col.description.substring(0, 120)}...
//                   </p>
//                   <div className={styles.colMeta}>
//                     <span>{col.resources?.length || 0} resources</span>
//                     <span>Tags: {col.tags?.join(', ') || 'None'}</span>
//                   </div>
//                   <div className={styles.colActions}>
//                     <Link to={`/collections/${col.slug}`} className={styles.btnView}>
//                       View Collection
//                     </Link>
//                     <button className={styles.btnEdit}>
//                       <Icon icon="lucide:edit" />
//                       Edit
//                     </button>
//                   </div>
//                 </div>
//               ))}
//             </div>

//             <div className={styles.addSection}>
//               <button className={styles.btnAdd}>
//                 <Icon icon="lucide:plus" />
//                 Create New Collection
//               </button>
//             </div>
//           </section>
//         )}
//       </div>
//     </main>
//   );
// }